import { Dispatch, ReactElement, SetStateAction, useState } from "react"
import moment from "moment"
import { SingleDatePicker } from "react-dates"
import Select from "react-select"
import { Button, Checkbox, Input, FormLabel } from "@wfp/ui"
import { SurveyHHFilters } from "interfaces/dataInterfaces"
import { useGetAllCategoriesQuery } from "services/household/categories"
import { useGetSubCategoriesQuery } from "services/household/subCategories"
import { useGetAttributesQuery } from "services/household/attributes"
import { useGetModalitiesQuery } from "services/household/modalities"
import { useGetOrganizationsQuery } from "services/household/organizations"
import { useGetAssessmentStatusesQuery, useGetPhasesQuery } from "services/household/survey"

interface Props {
  onApply: () => void
  onClear: () => void
  values: SurveyHHFilters
  setValues: Dispatch<SetStateAction<SurveyHHFilters>>
}

const AdvancedFilters = ({ onApply, onClear, values, setValues }: Props): ReactElement => {
  const [focused, setFocused] = useState({ startDate: false, endDate: false })

  const { data: categories, isFetching: isFetchingCategories } = useGetAllCategoriesQuery()
  const { data: attributes, isFetching: isFetchingAttributes } = useGetAttributesQuery()
  const { data: modalities, isFetching: isFetchingModalities } = useGetModalitiesQuery()
  const { data: statuses, isFetching: isFetchingStatuses } = useGetAssessmentStatusesQuery()
  const { data: phases, isFetching: isFetchingPhases } = useGetPhasesQuery()
  const { data: organizations, isFetching: isFetchingOrganizations } = useGetOrganizationsQuery()
  const { data: subCategories, isFetching: isFetchingSubCategories } = useGetSubCategoriesQuery(
    { categoryId: values.surveyCategoryIds[0] },
    { skip: !values.surveyCategoryIds[0] },
  )

  return (
    <div className="p-5 bg-slate-200 my-2 rounded-lg">
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 w-full">
        <div>
          <FormLabel htmlFor="category">Category</FormLabel>
          <Select
            inputId="category"
            placeholder="Select category"
            isLoading={isFetchingCategories}
            options={categories || []}
            getOptionLabel={option => option.name}
            isClearable
            onChange={item =>
              setValues({
                ...values,
                surveyCategoryIds: item?.value ? [Number(item.value)] : [],
                surveySubCategoryIds: [],
              })
            }
            value={categories?.find(cat => Number(cat.value) === values.surveyCategoryIds[0])}
          />
        </div>

        <div>
          <FormLabel htmlFor="type">Type</FormLabel>
          <Select
            inputId="type"
            isMulti
            isLoading={isFetchingSubCategories}
            placeholder="Select types"
            options={values.surveyCategoryIds.length && subCategories?.length ? subCategories : []}
            getOptionLabel={option => option.name}
            onChange={el => setValues({ ...values, surveySubCategoryIds: el.map(item => Number(item.value)) })}
            value={subCategories?.filter(subCat => values.surveySubCategoryIds.includes(Number(subCat.value)))}
          />
        </div>

        <div>
          <FormLabel htmlFor="modality">Modality</FormLabel>
          <Select
            inputId="modality"
            isMulti
            isLoading={isFetchingModalities}
            placeholder="Select modalities"
            options={modalities || []}
            getOptionLabel={option => option.name}
            onChange={el => setValues({ ...values, surveyModalityIds: el.map(item => Number(item.value)) })}
            value={modalities?.filter(mod => values.surveyModalityIds.includes(Number(mod.value)))}
          />
        </div>

        <div>
          <FormLabel htmlFor="attribute">Attribute</FormLabel>
          <Select
            inputId="attribute"
            isMulti
            isLoading={isFetchingAttributes}
            placeholder="Select attributes"
            options={attributes || []}
            getOptionLabel={option => option.name}
            onChange={el => setValues({ ...values, surveyAttributeIds: el.map(item => Number(item.value)) })}
            value={attributes?.filter(attr => values.surveyAttributeIds.includes(Number(attr.value)))}
          />
        </div>

        <div>
          <FormLabel htmlFor="status">Status</FormLabel>
          <Select
            inputId="status"
            isMulti
            isLoading={isFetchingStatuses}
            placeholder="Select statuses"
            options={statuses || []}
            getOptionLabel={option => option.name}
            onChange={el => setValues({ ...values, surveyAssessmentStatusIds: el.map(item => Number(item.value)) })}
            value={statuses?.filter(status => values.surveyAssessmentStatusIds.includes(Number(status.value)))}
          />
        </div>

        <div>
          <FormLabel htmlFor="phase">Phase</FormLabel>
          <Select
            inputId="phase"
            isMulti
            isLoading={isFetchingPhases}
            placeholder="Select phases"
            options={phases || []}
            getOptionLabel={option => option.name}
            onChange={el => setValues({ ...values, surveyPhaseIds: el.map(item => Number(item.value)) })}
            value={phases?.filter(phase => values.surveyPhaseIds.includes(Number(phase.value)))}
          />
        </div>

        <div>
          <FormLabel htmlFor="organization">Organization</FormLabel>
          <Select
            inputId="organization"
            isMulti
            isLoading={isFetchingOrganizations}
            placeholder="Select organizations"
            options={organizations || []}
            getOptionLabel={option => option.name}
            onChange={el => setValues({ ...values, organizationIds: el.map(item => Number(item.value)) })}
            value={organizations?.filter(org => values.organizationIds.includes(Number(org.value)))}
          />
        </div>

        <div className="flex items-center justify-between date-filters gap-4">
          <Input labelText="Start date" invalidText="A valid value is required">
            {() => (
              <SingleDatePicker
                displayFormat="DD/MM/YYYY"
                hideKeyboardShortcutsPanel
                showClearDate
                showDefaultInputIcon
                inputIconPosition="after"
                onDateChange={newDate => setValues({ ...values, startDate: newDate?.format("YYYY/MM/DD") || null })}
                focused={focused.startDate}
                onFocusChange={({ focused: onFocusChange }) => setFocused({ startDate: onFocusChange, endDate: false })}
                date={values.startDate ? moment(values.startDate, "YYYY/MM/DD") : null}
                id="start_date"
                isOutsideRange={m => {
                  if (!values.endDate) return false
                  return m.unix() > moment(values.endDate, "YYYY/MM/DD").unix()
                }}
              />
            )}
          </Input>

          <Input invalidText="A valid value is required" labelText="End date">
            {() => (
              <SingleDatePicker
                displayFormat="DD/MM/YYYY"
                hideKeyboardShortcutsPanel
                showClearDate
                showDefaultInputIcon
                inputIconPosition="after"
                onDateChange={newDate => setValues({ ...values, endDate: newDate?.format("YYYY/MM/DD") || null })}
                focused={focused.endDate}
                onFocusChange={({ focused: onFocusChange }) => setFocused({ startDate: false, endDate: onFocusChange })}
                date={values.endDate ? moment(values.endDate, "YYYY/MM/DD") : null}
                id="end_date"
                isOutsideRange={m => {
                  if (!values.startDate) return false
                  return m.unix() < moment(values.startDate, "YYYY/MM/DD").unix()
                }}
              />
            )}
          </Input>
        </div>

        <div className="mt-3 mx-1">
          <Checkbox
            checked={values.isContinuousMonitoring}
            id="isContinuousMonitoring"
            labelText="Continuous monitoring"
            value="continuous"
            onChange={() => setValues({ ...values, isContinuousMonitoring: !values.isContinuousMonitoring })}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button className="mr-2" onClick={onClear} kind="ghost">
          Clear filters
        </Button>
        <Button onClick={onApply}>Apply</Button>
      </div>
    </div>
  )
}

export default AdvancedFilters
